import './OfferRatePKO.scss';
import React, { useEffect, useState } from 'react';
import Button from '@atoms/Button';
import clsx from 'clsx';
import { MdClose } from 'react-icons/md';
import Modal from '@atoms/Modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm } from 'react-hook-form';
import { IOption } from '@/types';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@molecules/Notifications';
import Hint from '@atoms/Hint';
import { IOfferRatePKOFormData } from './IOfferRatePKOFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@controls/HookFormProvider';
import SelectControl from '@controls/SelectControl';

export interface IOfferRatePKOProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferRatePKOFormData) => void;
    onClose?: () => void;
    offerUnAcceptableHint?: string;
}

const resultOptions: IOption[] = [
    { label: 'Допущен', value: 'Допущен' },
    { label: 'Не допущен', value: 'Не допущен' },
];

const OfferRatePKO = ({
    tableKey,
    isEdit,
    value,
    demandId,
    onSubmit,
    onClose,
    offerUnAcceptableHint,
    ...props
}: IOfferRatePKOProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferRatePKOFormData>({
        mode: 'onBlur',
    });

    const { isSubmitting, isDirty } = methods.formState;

    useEffect(() => {
        const defaultValue: IOfferRatePKOFormData = {
            result: value.result,
        };
        methods.reset(defaultValue);
    }, [value]);

    const onClickClose = (e: any) => {
        onClose && onClose();
    };

    const onFormSubmit = async (form: IOfferRatePKOFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferRatePKO(value.key, demandId, tableKey, form)
            .then((response) => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const errorLabel = error && (
        <div className="offer-rate-pko__error">
            <Hint title={error} variant="red" />
        </div>
    );

    const offerUnAcceptableLabel = value.acceptanceResult !== 1 && offerUnAcceptableHint && (
        <div className={'offer-crit-details__hint'}>
            <Hint title={offerUnAcceptableHint} variant="yellow" maxWidth="100%" />
        </div>
    );

    const ModalHeader = ({ onClose }: { onClose: (e: any) => void }) => (
        <div className={clsx('offer-rate-pko-modal__header')}>
            <div className={clsx('offer-rate-pko-modal__header-text')}>
                <span>{value.name}</span>
            </div>
            <div className={clsx('offer-rate-pko-modal__header-close')}>
                <Button
                    buttonType="text"
                    textColor="neutral"
                    size="xs"
                    aria-label="Закрыть окно"
                    onClick={onClose}
                    startAdornment={<MdClose size="24" />}
                />
            </div>
        </div>
    );

    const ModalFooter = () => (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-rate-pko-form"
                preloader={isSubmitting}
                className={clsx('offer-rate-pko__button', 'offer-rate-pko__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal size="m" header={<ModalHeader onClose={onClickClose} />} footer={isEdit ? <ModalFooter /> : null}>
            {errorLabel}
            {offerUnAcceptableLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-rate-pko-form"
                className="offer-rate-pko__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-rate-pko__content">
                        <div className="offer-rate-pko__row">
                            <SelectControl
                                label="Результат"
                                name="result"
                                readOnly={true}
                                disabled={!isEdit}
                                required={true}
                                rules={{ required: 'Обязательное поле' }}
                                highlightMenuItems={false}
                                options={resultOptions}
                                formState={methods.formState}
                                control={methods.control}
                                setValue={(options) => {
                                    return options[0].value;
                                }}
                                getValue={(options: IOption[], value: any) => {
                                    return options.filter((x) => x.value === value);
                                }}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferRatePKO;
